/**
 * How a stored notification is rendered and where clicking it goes.
 *
 * Notification rows are written by the backend in English (they are also read
 * by scripts and support), so anything the user should see in their own
 * language is re-rendered here from `metadata`, with the stored title/message
 * as the fallback for older or unrecognised rows.
 */

import { localizeSeeded } from '../pages/FlowManagement/flowUtils';

// Where a notification takes you. Types with a dedicated destination are listed
// explicitly; everything else falls back to its entity.
export function notificationTarget(n) {
  if (!n) return null;

  if (n.type === 'user_deactivated_workload') {
    const userId = n.metadata?.userId || n.entityId;
    return userId ? `/task-reassignment?userId=${userId}` : '/task-reassignment';
  }
  if (n.type === 'task_reassigned') {
    return n.entityId ? `/contracts/${n.entityId}` : '/approvals';
  }
  if (n.entityType === 'contract' && n.entityId) return `/contracts/${n.entityId}`;

  return null;
}

// Localized title/message for the types we know how to render; stored text
// otherwise.
export function notificationText(n, t, lang) {
  if (n?.type === 'user_deactivated_workload') {
    const m = n.metadata || {};
    // Both halves of the reason are optional — only one of them has to be true
    // for the notification to exist, so the message is assembled from whichever
    // applies.
    const reasons = [];
    if (m.activeTaskCount > 0) {
      reasons.push(t('notifications.userDeactivated.tasks', { count: m.activeTaskCount }));
    }
    if (m.workflowCount > 0) {
      reasons.push(t('notifications.userDeactivated.workflows', { count: m.workflowCount }));
    }
    return {
      title: t('notifications.userDeactivated.title', { name: m.userName || n.title }),
      message: reasons.length
        ? t('notifications.userDeactivated.message', {
          name: m.userName || '',
          detail: reasons.join(t('notifications.userDeactivated.and')),
        })
        : n.message,
      detail: (m.workflows || []).map((w) => localizeSeeded(w.workflowName, lang)).filter(Boolean),
    };
  }

  return { title: n?.title, message: n?.message, detail: [] };
}
